import { useState } from 'react';
import { Switch } from './ui/switch';
import { CopySlotPricing } from './CopySlotPricing';
import { SlotFeePickers, priceSummary, rushDays, cleanFees } from './SlotFeePickers';

export const TIER_LABELS = { standard: 'New', silver: 'Silver', gold: 'Gold', platinum: 'Platinum', onyx: 'Onyx', black_card: 'Black Card' };
const WAIVERS = [0, 50, 100];
const ALL_FEES = ['base', 'rush', 'long_distance'];
const COVER_NAMES = { base: 'base', rush: 'rush', long_distance: 'long dist.' };

const rangeLabel = (h) => {
  const f = (x) => `${x % 12 || 12}${x < 12 ? 'am' : 'pm'}`;
  return `${f(h)}–${f((h + 1) % 24)}`;
};
const inputCls = 'bg-[#050003] border border-[#2A0F3D] focus:border-[#39FF14] font-mono text-sm px-2 py-1 text-white outline-none rounded-none';

const FEE_CHIPS = [['base', 'Base'], ['rush', 'Rush'], ['long_distance', 'Long dist.']];

function WaiverPicker({ hour, tier, value, covers, onChange, onCovers }) {
  const toggle = (k) => onCovers(covers.includes(k) ? covers.filter((x) => x !== k) : [...covers, k]);
  return (
    <div className="flex flex-col gap-1" data-testid={`slot-${hour}-waiver-${tier}`}>
      <div className="flex items-center justify-between gap-2">
      <span className="font-mono text-xs text-[#A3C7A8] w-20">{TIER_LABELS[tier]}</span>
      <div className="flex">
        {WAIVERS.map((w) => (
          <button key={w} type="button" onClick={() => onChange(w)}
            className={`px-2 py-0.5 font-mono text-[11px] border -ml-px ${value === w ? 'border-[#39FF14] text-[#050003] bg-[#39FF14]' : 'border-[#2A0F3D] text-[#6B8E73] hover:text-[#39FF14]'}`}
            data-testid={`slot-${hour}-waiver-${tier}-${w}`}>
            {w}% off
          </button>
        ))}
      </div>
      </div>
      {value > 0 && (
        <div className="flex gap-1 justify-end items-center">
          <span className="font-mono text-[10px] text-[#6B8E73] mr-1">applies to</span>
          {FEE_CHIPS.map(([k, n]) => (
            <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={covers.includes(k)}
              className={`px-1.5 py-0.5 font-mono text-[10px] border ${covers.includes(k) ? 'border-[#00F0FF] text-[#00F0FF]' : 'border-[#2A0F3D] text-[#4B6B52] line-through'}`}
              data-testid={`slot-${hour}-waiver-${tier}-covers-${k}`}>{n}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function TierAccess({ hour, tiers, value, onChange }) {
  const toggle = (t) => onChange(value.includes(t) ? value.filter((x) => x !== t) : [...value, t]);
  return (
    <div>
      <p className="font-mono text-[10px] text-[#6B8E73] uppercase mb-1">Who can book · {value.length ? 'selected tiers only' : 'all tiers'}</p>
      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => onChange([])} className={`px-2 py-0.5 font-mono text-[11px] border ${!value.length ? 'border-[#00F0FF] text-[#00F0FF]' : 'border-[#2A0F3D] text-[#6B8E73]'}`} data-testid={`slot-${hour}-access-all`}>All</button>
        {tiers.map((t) => (
          <button key={t} type="button" onClick={() => toggle(t)} className={`px-2 py-0.5 font-mono text-[11px] border ${value.includes(t) ? 'border-[#00F0FF] text-[#00F0FF]' : 'border-[#2A0F3D] text-[#6B8E73]'}`} data-testid={`slot-${hour}-access-${t}`}>{TIER_LABELS[t]}</button>
        ))}
      </div>
    </div>
  );
}

const waiverSummary = (slot, tiers) => {
  const w = tiers.filter((t) => slot.waivers?.[t]).map((t) => {
    const c = slot.waiver_fees?.[t];
    const part = c && c.length < 3 ? ` on ${c.map((k) => COVER_NAMES[k]).join(' + ') || 'nothing'}` : '';
    return `${TIER_LABELS[t]} ${slot.waivers[t]}%${part}`;
  });
  return w.length ? `Waivers: ${w.join(', ')}` : 'No waivers';
};

export function DeliverySlotRow({ slot, tiers, onSave, onToggle, slots, onCopy }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const d = draft || slot;
  const set = (k, v) => setDraft({ ...d, [k]: v });
  const save = async () => {
    await onSave(slot.hour, { label: d.label, waivers: d.waivers, waiver_fees: d.waiver_fees || {}, allowed_tiers: d.allowed_tiers || [], fees: cleanFees(d.fees) });
    setDraft(null);
  };
  const h = slot.hour;
  const rush = rushDays(slot.fees);
  return (
    <div className={`border ${slot.active ? 'border-[#39FF14]/50' : 'border-[#2A0F3D]'} bg-[#0C0612]`} data-testid={`slot-row-${h}`}>
      <div className="flex items-center gap-3 p-3 flex-wrap">
        <Switch checked={!!slot.active} onCheckedChange={(v) => onToggle(h, v)} data-testid={`slot-${h}-active`} />
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left" data-testid={`slot-${h}-expand`}>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`font-mono text-sm font-bold ${slot.active ? 'text-[#39FF14]' : 'text-[#6B8E73]'}`}>{rangeLabel(h)}</span>
            {slot.label && <span className="font-mono text-xs text-[#FFD700]">{slot.label}</span>}
            {rush && <span className="font-mono text-[10px] text-[#FF9F0A] border border-[#FF9F0A]/60 px-1 whitespace-nowrap" data-testid={`slot-${h}-rush-badge`}>{(slot.fees?.rush?.label || '').trim().replace(/\s+fee$/i, '') || 'Rush hour'} {rush}</span>}
            {(slot.allowed_tiers || []).length > 0 && <span className="font-mono text-[10px] text-[#00F0FF] border border-[#00F0FF]/50 px-1">{slot.allowed_tiers.map((t) => TIER_LABELS[t]).join(' + ')} only</span>}
          </div>
          <p className="font-mono text-[11px] text-[#A3C7A8] break-words" data-testid={`slot-${h}-summary`}>
            {priceSummary(slot.fees)} · {waiverSummary(slot, tiers)}
          </p>
        </button>
        <span className="font-mono text-[10px] text-[#6B8E73]">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div className="border-t border-[#2A0F3D] p-3 space-y-4" data-testid={`slot-${h}-editor`}>
          <label className="font-mono text-[10px] text-[#6B8E73] uppercase flex flex-col gap-1">
            Label (blank = time range)
            <input value={d.label || ''} maxLength={40} onChange={(e) => set('label', e.target.value)} placeholder={rangeLabel(h)} className={`${inputCls} w-full max-w-xs`} data-testid={`slot-${h}-label`} />
          </label>
          <SlotFeePickers hour={h} fees={d.fees} onChange={(v) => set('fees', v)} />
          <div className="space-y-1.5">
            <p className="font-mono text-[10px] text-[#6B8E73] uppercase">Tier fee waiver <span className="normal-case">(tap the fee chips to choose what each waiver covers)</span></p>
            {tiers.map((t) => (
              <WaiverPicker key={t} hour={h} tier={t} value={d.waivers?.[t] || 0} covers={d.waiver_fees?.[t] || ALL_FEES}
                onChange={(w) => set('waivers', { ...d.waivers, [t]: w })}
                onCovers={(c) => set('waiver_fees', { ...(d.waiver_fees || {}), [t]: c })} />
            ))}
          </div>
          <TierAccess hour={h} tiers={tiers} value={d.allowed_tiers || []} onChange={(v) => set('allowed_tiers', v)} />
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={!draft} onClick={save} className="px-3 py-1.5 font-mono text-xs border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-40" data-testid={`slot-${h}-save`}>Save slot</button>
            {draft && <button type="button" onClick={() => setDraft(null)} className="px-3 py-1.5 font-mono text-xs text-[#6B8E73]" data-testid={`slot-${h}-discard`}>Discard</button>}
            {onCopy && !draft && <CopySlotPricing hour={h} slots={slots} onCopy={onCopy} />}
          </div>
        </div>
      )}
    </div>
  );
}
